/**
 * QuickAddUserLink - the "+ Quick Add User" link under a people picker.
 *
 * Atomic Design role: Molecule. A LinkButton wired to QuickAddUsersContext:
 * pressing it opens the app's one Quick Add User modal (mounted by
 * QuickAddUsersProvider) for this picker, and the people it saves come back
 * through `onCreated`. Renders nothing when the viewer's role can't create
 * users, so a technician never sees a link the API would refuse.
 *
 * Pickers pair it with the dropdown's footer action (see
 * `useQuickAddFooterAction`), so the same modal opens from under the field
 * and from inside the open list.
 *
 * @example
 * <QuickAddUserLink defaultCompanyId={companyId} onCreated={(people) => addRows(people)} />
 */
import React from 'react';
import { LinkButton } from '../../atoms/LinkButton';
import type { DropdownFooterActionConfig } from '../../atoms/DropdownFooterAction';
import { useQuickAddUsers } from '../../../context/QuickAddUsersContext';
import type { QuickAddedPersonRef } from '../../../utils/quickAddUser';

/** The visible label, shared by the link and the dropdown action. */
export const QUICK_ADD_USER_LABEL = '+ Quick Add User';

export interface QuickAddUserLinkProps {
	/** The work order's business — the default primary business for each person. */
	defaultCompanyId?: string | number | null;
	/** Receives everyone the modal saves. */
	onCreated: (people: QuickAddedPersonRef[]) => void;
	/** One person only (e.g. a contact). */
	single?: boolean;
	/** Optional extra classes on the link. */
	className?: string;
}

export const QuickAddUserLink: React.FC<QuickAddUserLinkProps> = ({ defaultCompanyId, onCreated, single = false, className = '' }) => {
	const { canQuickAdd, openQuickAdd } = useQuickAddUsers();
	if (!canQuickAdd) return null;
	return (
		<LinkButton onClick={() => openQuickAdd({ defaultCompanyId, onCreated, single })} className={`quick-add-user-link ${className}`.trim()} dataRef="molecule-quick-add-user-link">
			{QUICK_ADD_USER_LABEL}
		</LinkButton>
	);
};

/**
 * The matching dropdown footer action for a picker, or undefined when the
 * viewer can't quick-add. Whatever was typed into the search pre-fills the
 * new person's name.
 */
export function useQuickAddFooterAction(options: Omit<QuickAddUserLinkProps, 'className'> | null): DropdownFooterActionConfig | undefined {
	const { canQuickAdd, openQuickAdd } = useQuickAddUsers();
	if (!canQuickAdd || !options) return undefined;
	return {
		label: QUICK_ADD_USER_LABEL,
		onSelect: (searchText) => openQuickAdd({ defaultCompanyId: options.defaultCompanyId, onCreated: options.onCreated, single: options.single, initialName: searchText }),
	};
}

export default QuickAddUserLink;
